import { ReactNode } from 'react';
import styled from 'styled-components';
import { Dialog as DialogBase } from '@mui/material';
import Typography from '@mui/material/Typography';
import { ReactComponent as CloseIcon } from '../../assets/close.svg';
import ButtonBase, { LoadingButton, Props as ButtonProps } from '../Button';
import { Flex, FlexSpace, FlexColumn } from '../Flex';
import OverflowTooltip from '../OverflowTooltip';
import IconButton from '../IconButton';

const Wrapper = styled(DialogBase)`
  & .MuiDialog-paper {
    min-width: 540px;
    width: ${({ $customWidth }: { $customWidth?: number }) =>
      $customWidth ? `${$customWidth}px` : ''};
    max-width: 100%;
  }
`;

const Title = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 16px;
  background-color: ${({ theme }) => theme.palette.common.gray6};
`;

const Content = styled(FlexColumn)`
  gap: 16px;
  padding: 20px 24px;
  overflow: hidden;
  overflow-y: auto;
`;

const ButtonContainer = styled(Flex)`
  line-height: 42px;
  text-align: center;
  gap: 16px;
`;

const Button = styled(ButtonBase)`
  margin-left: 30px;
`;

const Footer = styled(FlexSpace)<{ $showDivider?: boolean }>`
  justify-content: flex-end;
  gap: 16px;
  ${({ theme, $showDivider }) =>
    $showDivider && `border-top: 1px solid ${theme.palette.common.gray8};`}
  padding: 16px 24px;
`;

type Props = {
  open: boolean;
  onClose: () => void;
  onCancelButtonClick?: () => void;
  onConfirmButtonClick?: () => void;
  isLoading?: boolean;
  titleIcon?: ReactNode;
  title: string;
  titleChip?: ReactNode;
  exitButton?: boolean;
  message?: string | ReactNode;
  secondaryMessage?: string | ReactNode;
  children?: ReactNode;
  cancelText?: string;
  confirmText?: string;
  confirmDisabled?: boolean;
  hideCancelButton?: boolean;
  hideConfirmButton?: boolean;
  graphic?: React.ReactNode;
  footerMessage?: React.ReactNode;
  footerRight?: React.ReactNode;
  footerDivider?: boolean;
  customWidth?: number;
  actionButtonText?: string;
  actionButtonDisabled?: boolean;
  actionButtonColor?: ButtonProps['color'];
  actionButtonVariant?: ButtonProps['variant'];
  onActionButtonClick?: () => void;
  closeOnBackdropClick?: boolean;
  variant?: 'default' | 'error';
};

const Dialog = ({
  open,
  onClose,
  onCancelButtonClick,
  onConfirmButtonClick,
  isLoading,
  titleIcon,
  title,
  titleChip,
  message,
  secondaryMessage,
  children,
  exitButton = true,
  cancelText = 'Cancel',
  confirmText,
  confirmDisabled,
  hideCancelButton = false,
  hideConfirmButton = false,
  graphic,
  footerMessage,
  footerRight,
  footerDivider = true,
  customWidth,
  actionButtonText,
  actionButtonDisabled,
  onActionButtonClick,
  actionButtonColor = 'secondary',
  actionButtonVariant = 'text',
  closeOnBackdropClick = true,
  variant = 'default',
}: Props) => (
  <Wrapper
    open={open}
    onClose={(_, reason) => {
      if (!closeOnBackdropClick && reason === 'backdropClick') {
        return;
      }
      onClose();
    }}
    $customWidth={customWidth}
    maxWidth={customWidth ? false : undefined}
  >
    <Title>
      <Flex sx={{ gap: 1, alignItems: 'center' }}>
        {titleIcon && titleIcon}
        <Typography sx={{ fontWeight: 'bold' }}>{title}</Typography>
        {titleChip}
      </Flex>

      {exitButton && (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
        >
          <CloseIcon />
        </IconButton>
      )}
    </Title>

    <Content>
      {graphic}

      <FlexColumn sx={{ gap: 1 }}>
        {typeof message === 'string' ? (
          <Typography sx={{ fontSize: '12px' }}>{message}</Typography>
        ) : (
          message
        )}

        {secondaryMessage && typeof secondaryMessage === 'string' ? (
          <Typography sx={{ fontSize: '12px', color: 'common.gray4' }}>
            {secondaryMessage}
          </Typography>
        ) : (
          secondaryMessage
        )}
      </FlexColumn>

      {children}
    </Content>

    <Footer $showDivider={footerDivider}>
      {footerRight ? (
        <Flex sx={{ flex: 1, alignItems: 'center' }}>{footerRight}</Flex>
      ) : (
        <>
          {footerMessage && (
            <OverflowTooltip color="common.gray4" sx={{ fontSize: '12px' }}>
              {footerMessage}
            </OverflowTooltip>
          )}
          {actionButtonText && (
            <ButtonBase
              color={actionButtonColor}
              variant={actionButtonVariant}
              onClick={onActionButtonClick}
              disabled={actionButtonDisabled}
            >
              {actionButtonText}
            </ButtonBase>
          )}
          <span style={{ flex: 1 }} />
        </>
      )}

      <ButtonContainer>
        {!hideCancelButton && (
          <Button
            variant="outlined"
            color="secondary"
            onClick={(e) => {
              e.stopPropagation();
              (onCancelButtonClick ?? onClose)();
            }}
          >
            {cancelText}
          </Button>
        )}
        {!hideConfirmButton && (
          <LoadingButton
            onClick={(e) => {
              e.stopPropagation();
              onConfirmButtonClick?.();
            }}
            loading={isLoading}
            disabled={confirmDisabled}
            color={variant === 'error' ? 'error' : 'primary'}
          >
            {confirmText}
          </LoadingButton>
        )}
      </ButtonContainer>
    </Footer>
  </Wrapper>
);

export default Dialog;
